"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import ImageLibraryPanel from "./ImageLibraryPanel";
import ClipartLibraryPanel from "./ClipartLibraryPanel";
import type { ClipartItem } from "./ClipartLibraryPanel";
import TemplateThumbnail from "./TemplateThumbnail";
import { useEditorStore } from "./editorStore";
import type { ShapeId } from "./shapePresets";
import type { HelpAction } from "../help/faq";
import { getSpreadTemplates } from "../templates";
import type { FormatGroup, ThemeTemplate } from "../templates/types";
import { BACKGROUND_PATTERNS, getBackgroundPatternUrl } from "../backgrounds/patternLibrary";

const COLLAPSED_TOOLS_WIDTH = 104;
const TOP_POPUP_Y = 72;

const SHAPE_MENU: { id: ShapeId; label: string }[] = [
  { id: "rectangle", label: "Rectangle" },
  { id: "circle", label: "Circle" },
  { id: "triangle", label: "Triangle" },
  { id: "rhombus", label: "Rhombus" },
  { id: "pentagon", label: "Pentagon" },
  { id: "octagon", label: "Octagon" },
  { id: "star", label: "Star" },
  { id: "kite", label: "Kite" },
  { id: "heart", label: "Heart" },
  { id: "crescent", label: "Crescent" },
  { id: "oval", label: "Oval" },
  { id: "semicircle", label: "Semicircle" }
];

function ShapeIcon({ id }: { id: string }) {
  const common = { fill: "currentColor" };

  const polys: Record<string, string> = {
    triangle: "50,0 100,100 0,100",
    rhombus: "50,0 100,50 50,100 0,50",
    pentagon: "50,0 100,38 81,100 19,100 0,38",
    octagon: "30,0 70,0 100,30 100,70 70,100 30,100 0,70 0,30",
    star: "50,0 61,35 98,35 68,57 79,91 50,70 21,91 32,57 2,35 39,35",
    kite: "50,0 86,40 50,100 14,40"
  };

  const paths: Record<string, { d: string; fillRule?: "nonzero" | "evenodd" }> = {
    crescent: { d: "M77.20 22.94 A35 35 0 1 0 77.20 77.06 A28 28 0 1 1 77.20 22.94 Z" },
    heart: { d: "M50 92 C22 74 10 58 10 38 C10 20 24 10 38 10 C46 10 49 16 50 22 C51 16 54 10 62 10 C76 10 90 20 90 38 C90 58 78 74 50 92 Z" },
    oval: { d: "M50 15 A40 30 0 1 0 50 85 A40 30 0 1 0 50 15 Z" },
    semicircle: { d: "M10 60 A40 40 0 0 1 90 60 L90 100 L10 100 Z" }
  };

  return (
    <svg viewBox="0 0 100 100" className="h-8 w-8" aria-hidden="true">
      {id === "rectangle" ? <rect x="10" y="10" width="80" height="80" rx="10" {...common} /> : null}
      {id === "circle" ? <circle cx="50" cy="50" r="40" {...common} /> : null}
      {polys[id] ? <polygon points={polys[id]} {...common} /> : null}
      {paths[id] ? <path d={paths[id].d} fillRule={paths[id].fillRule || "nonzero"} {...common} /> : null}
    </svg>
  );
}

function TextToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6h12M12 6v12M9 18h6" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function ImageToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="1" />
      <circle cx="9" cy="9" r="1.7" fill="currentColor" />
      <path d="m20 15-4.5-4.5L7 19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClipartToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 19.5h12a2 2 0 0 0 2-2v-11a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2Z" stroke="currentColor" strokeWidth="1" />
      <path d="M8 15.5 10.8 12l2.2 2.4 2.8-4.1L20 15.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8.4" cy="8.4" r="1.4" fill="currentColor" />
    </svg>
  );
}

function ShapesToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14.5 4.5h5v5h-5z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
      <circle cx="7.5" cy="16.5" r="3.5" stroke="currentColor" strokeWidth="1" />
      <path d="M8 4.5 12 11H4L8 4.5Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m15 14 5 5M20 14l-5 5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function BackgroundToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="m5 12.5 6.7-6.7a2 2 0 0 1 2.8 0l3.7 3.7a2 2 0 0 1 0 2.8L12 18.5a2.2 2.2 0 0 1-3.1 0L5 14.6a1.5 1.5 0 0 1 0-2.1Z" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.2 9.3h8.1M4.8 13.4h14.4" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <path d="M19 16.2s2 2.1 2 3.4a2 2 0 0 1-4 0c0-1.3 2-3.4 2-3.4Z" stroke="currentColor" strokeWidth="1" strokeLinejoin="round" />
    </svg>
  );
}

function TemplatesToolIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3.5" y="4" width="17" height="16" rx="2" stroke="currentColor" strokeWidth="1" />
      <path d="M8.75 4v16M15.25 4v16M3.5 9.5h17M3.5 14.5h11.75" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <path d="M15.25 14.5h5.25" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );
}

function getThemeThumbnailUrl(theme: ThemeTemplate) {
  return theme.thumbnail || `/template-thumbnails/themes/${theme.id}.png`;
}

function ThemeTemplateThumbnail({
  theme,
  pagesLabel,
}: {
  theme: ThemeTemplate;
  pagesLabel: string;
}) {
  const [failed, setFailed] = useState(false);
  const url = getThemeThumbnailUrl(theme);

  if (failed) {
    return (
      <div className="flex h-24 w-full items-center justify-center bg-gradient-to-br from-[#eef2ff] via-white to-[#ecfeff] px-3 text-center">
        <div>
          <div className="text-xs font-semibold text-[#27324a]">{theme.name}</div>
          <div className="mt-1 text-[11px] text-[#64748b]">{pagesLabel}</div>
        </div>
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      className="h-24 w-full object-cover"
      draggable={false}
      onError={() => setFailed(true)}
    />
  );
}

function BackgroundPatternPreview(props: {
  url: string;
  tileW: number;
  selected?: boolean;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const { url, tileW, selected, title, onClick, disabled } = props;
  return (
    <button
      type="button"
      className={`overflow-hidden rounded-xl border border-white/10 bg-white/5 transition-all hover:bg-white/10 ${
        selected ? "ring-2 ring-white/30" : ""
      }`}
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
    >
      <div
        className="h-24 w-full"
        style={{
          backgroundColor: "#FFFFFF",
          backgroundImage: `url(${url})`,
          backgroundRepeat: "repeat",
          backgroundSize: `${Math.max(64, Math.round(tileW * 0.55))}px auto`,
        }}
      />
    </button>
  );
}

function normalizeHexColor(value: unknown, fallback = "#fefefe") {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  if (/^#[0-9a-fA-F]{3}$/.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`;
  }
  return fallback;
}

type ActiveTool = "text" | "image" | "clipart" | "shapes" | "background" | "templates" | null;

export default function RightToolsPanel({
  onAddTextRole,
  onAddShape,
  onAddRect,
  onAddBackground,
  backgroundColor,
  onApplySolidBackgroundColor,
  onApplyBackgroundPattern,
  showImages,
  onOpenImages,
  onCloseImages,
  onInsertLibraryAsset,
  onInsertClipart,
  onUploadImage,
  onBulkUpload,
  imageLibraryRefreshNonce,
  themeTemplates,
  onApplyTheme,
  onInsertSpreadTemplate,
  canCreateThemeTemplate,
  onCreateThemeTemplate,
  themeTemplateCreateBusy,
  onDeleteThemeTemplate,
  themeTemplateDeleteBusyId,
  activeFormatGroup,
  uploading,
  bulkProgress,
  canUndoTheme,
  onUndoTheme,
  readOnly,
  helpAction,
  helpActionNonce,
  presenceLabel,
  propertiesPanel,
  launcherOnly = false,
  collapsed = false,
  onCollapsedChange,
}: {
  onAddTextRole: (role: "heading" | "body" | "list") => void;
  onAddShape: (shapeId: ShapeId) => void;
  onAddRect: () => void;
  onAddBackground: () => void;
  backgroundColor: string;
  onApplySolidBackgroundColor: (color: string) => void;
  onApplyBackgroundPattern: (patternId: string) => void;
  showImages: boolean;
  onOpenImages: () => void;
  onCloseImages: () => void;
  onInsertLibraryAsset?: (asset: any) => Promise<void> | void;
  onInsertClipart?: (item: ClipartItem) => Promise<void> | void;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBulkUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  imageLibraryRefreshNonce?: number;
  themeTemplates: ThemeTemplate[];
  onApplyTheme: (themeId: string) => void;
  onInsertSpreadTemplate: (templateId: string) => void;
  canCreateThemeTemplate?: boolean;
  onCreateThemeTemplate?: (input: { name: string; tipsText?: string }) => Promise<void> | void;
  themeTemplateCreateBusy?: boolean;
  onDeleteThemeTemplate?: (themeId: string) => Promise<void> | void;
  themeTemplateDeleteBusyId?: string | null;
  activeFormatGroup: FormatGroup;
  uploading: boolean;
  bulkProgress?: { total: number; done: number; running: boolean };
  canUndoTheme: boolean;
  onUndoTheme: () => void;
  readOnly?: boolean;
  helpAction?: HelpAction | null;
  helpActionNonce?: number;
  presenceLabel?: string;
  propertiesPanel?: React.ReactNode;
  launcherOnly?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}) {
  const t = useTranslations("editor.tools");
  const locale = useLocale();
  const store = useEditorStore();
  const [showShapes, setShowShapes] = useState(false);
  const [showClipart, setShowClipart] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showBackground, setShowBackground] = useState(false);
  const [backgroundPanelMode, setBackgroundPanelMode] = useState<"patterns" | "solid">("patterns");
  const [shapesEntered, setShapesEntered] = useState(false);
  const [imagesEntered, setImagesEntered] = useState(false);
  const [clipartEntered, setClipartEntered] = useState(false);
  const [templatesEntered, setTemplatesEntered] = useState(false);
  const [backgroundEntered, setBackgroundEntered] = useState(false);
  const [templatesTab, setTemplatesTab] = useState<"themes" | "spreads">("themes");
  const [themeTemplateName, setThemeTemplateName] = useState("");
  const [themeTemplateTipText, setThemeTemplateTipText] = useState("");
  const [copiedThemeTemplateId, setCopiedThemeTemplateId] = useState<string | null>(null);
  const [imageAssetDragging, setImageAssetDragging] = useState(false);
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);
  const [activeTextRole, setActiveTextRole] = useState<"heading" | "body" | "list" | null>(null);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const handledHelpActionNonceRef = useRef<number | undefined>(undefined);
  const toolsCollapsed = collapsed && !launcherOnly;
  const spreads = useMemo(() => getSpreadTemplates(activeFormatGroup), [activeFormatGroup]);
  const activeDocData = store.activeDoc === "content" ? store.contentData : store.coverData;
  const activePageIndex = store.activeDoc === "content" ? store.contentUnitIndex : store.coverPageIndex;
  const activePage = Array.isArray(activeDocData?.pages) ? activeDocData.pages[activePageIndex] : null;
  const activeBackground = Array.isArray(activePage?.elements)
    ? activePage.elements.find((el: any) => el?.type === "background" || el?.is_background)
    : null;
  const selectedPatternId =
    String(activeBackground?.style?.background_kind || "solid") === "pattern"
      ? String(activeBackground?.style?.pattern_id || "")
      : "";
  const activeBackgroundColor = normalizeHexColor(activeBackground?.style?.fill_hex || backgroundColor);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobileViewport(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  function floatingPanelClass(entered: boolean) {
    if (isMobileViewport) {
      return `editor-modal fixed inset-x-0 bottom-0 z-[10000] max-h-[82dvh] w-full overflow-hidden rounded-t-2xl border-t border-white/10 transition-transform duration-200 ease-out ${
        entered ? "translate-y-0" : "translate-y-3"
      }`;
    }

    return `editor-modal fixed z-[10000] h-[calc(100vh-72px)] w-[330px] max-w-[92vw] overflow-hidden border-r border-t-[3px] transition-transform duration-200 ease-out ${
      entered ? "translate-x-0" : "-translate-x-2"
    }`;
  }

  function floatingPanelStyle(): React.CSSProperties | undefined {
    return isMobileViewport
      ? undefined
      : { left: toolsCollapsed ? COLLAPSED_TOOLS_WIDTH : 0, top: TOP_POPUP_Y };
  }

  function floatingOverlayClass() {
    if (isMobileViewport || !toolsCollapsed) return "fixed inset-0 z-[9999]";
    return "fixed bottom-0 right-0 top-0 z-[9999] left-[104px]";
  }

  function closeToolPanel(closePanel: () => void) {
    closePanel();
    setActiveTool(null);
    onCollapsedChange?.(false);
  }

  useEffect(() => {
    if (!readOnly) return;
    setShowShapes(false);
    setShowClipart(false);
    setShowTemplates(false);
    setShowBackground(false);
    onCloseImages();
    onCollapsedChange?.(false);
  }, [onCloseImages, onCollapsedChange, readOnly]);

  const descriptions = useMemo(
    () =>
      locale === "pl"
        ? {
            text: "Nagłówki, akapity, listy",
            image: "Dodaj i edytuj obrazy",
            clipart: "Gotowe ilustracje",
            shapes: "Kształty i elementy",
            background: "Kolory i wzory",
            templates: "Motywy i układy stron",
            textBody: "Tekst",
            textHeading: "Nagłówki",
            textList: "Lista",
          }
        : {
            text: "Heading, paragraphs, lists",
            image: "Add & edit images",
            clipart: "Ready-made illustrations",
            shapes: "Shapes & elements",
            background: "Colors & patterns",
            templates: "Themes and page layouts",
            textBody: "Text",
            textHeading: "Headings",
            textList: "List",
          },
    [locale]
  );

  useEffect(() => {
    if (!showShapes) return;

    setShapesEntered(false);
    const frame = window.requestAnimationFrame(() => {
      setShapesEntered(true);
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeToolPanel(() => setShowShapes(false));
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showShapes]);

  useEffect(() => {
    if (!showImages) return;

    setImagesEntered(false);
    const frame = window.requestAnimationFrame(() => {
      setImagesEntered(true);
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeToolPanel(onCloseImages);
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onCloseImages, showImages]);

  useEffect(() => {
    if (!imageAssetDragging) return;
    const stopDragging = () => setImageAssetDragging(false);
    window.addEventListener("dragend", stopDragging);
    window.addEventListener("drop", stopDragging);
    window.addEventListener("pointerup", stopDragging);
    window.addEventListener("pointercancel", stopDragging);
    return () => {
      window.removeEventListener("dragend", stopDragging);
      window.removeEventListener("drop", stopDragging);
      window.removeEventListener("pointerup", stopDragging);
      window.removeEventListener("pointercancel", stopDragging);
    };
  }, [imageAssetDragging]);

  useEffect(() => {
    if (!showClipart) {
      setClipartEntered(false);
      return;
    }

    setClipartEntered(false);
    const frame = window.requestAnimationFrame(() => {
      setClipartEntered(true);
    });

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeToolPanel(() => setShowClipart(false));
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showClipart]);

  useEffect(() => {
    if (!showTemplates) {
      setTemplatesEntered(false);
      return;
    }

    const tt = window.setTimeout(() => setTemplatesEntered(true), 10);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeToolPanel(() => setShowTemplates(false));
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(tt);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showTemplates]);

  useEffect(() => {
    if (!showBackground) {
      setBackgroundEntered(false);
      return;
    }

    setBackgroundPanelMode("patterns");

    const tt = window.setTimeout(() => setBackgroundEntered(true), 10);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeToolPanel(() => setShowBackground(false));
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.clearTimeout(tt);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [showBackground]);

  useEffect(() => {
    if (!helpAction) return;
    if (!helpActionNonce) return;
    if (handledHelpActionNonceRef.current === helpActionNonce) return;
    handledHelpActionNonceRef.current = helpActionNonce;

    // English comment: Open the appropriate tool/modal from Help deeplink.
    if (helpAction.type === "open_tool") {
      setActiveTool(helpAction.tool);
      onCollapsedChange?.(true);
      setShowTemplates(false);
      setShowBackground(false);
      setShowClipart(false);

      if (helpAction.tool === "image") {
        setShowShapes(false);
        onOpenImages();
        return;
      }

      onCloseImages();

      if (helpAction.tool === "clipart") {
        setShowShapes(false);
        setShowClipart(true);
        return;
      }

      if (helpAction.tool === "shapes") {
        setShowShapes(true);
        return;
      }

      setShowShapes(false);

      if (helpAction.tool === "background") {
        setShowBackground(true);
        return;
      }

      if (helpAction.tool === "text" && helpAction.textRole) {
        setActiveTextRole(helpAction.textRole);
        return;
      }

      return;
    }

    if (helpAction.type === "open_templates") {
      setActiveTool("templates");
      onCollapsedChange?.(true);
      onCloseImages();
      setShowShapes(false);
      setShowClipart(false);
      setShowBackground(false);
      setTemplatesTab(helpAction.tab);
      setShowTemplates(true);
    }
  }, [helpAction, helpActionNonce, onCloseImages, onCollapsedChange, onOpenImages]);

  async function handleCreateThemeTemplate() {
    const name = themeTemplateName.trim();
    if (!name || !onCreateThemeTemplate || themeTemplateCreateBusy) return;
    await onCreateThemeTemplate({ name, tipsText: themeTemplateTipText });
    setThemeTemplateName("");
    setThemeTemplateTipText("");
  }

  async function copyThemeTemplateId(themeId: string) {
    try {
      await navigator.clipboard.writeText(themeId);
      setCopiedThemeTemplateId(themeId);
      window.setTimeout(() => {
        setCopiedThemeTemplateId((current) => (current === themeId ? null : current));
      }, 1600);
    } catch {
      window.prompt(locale === "pl" ? "Skopiuj ID motywu:" : "Copy theme ID:", themeId);
    }
  }

  const tools = [
    {
      id: "text" as const,
      label: t("text"),
      description: descriptions.text,
      icon: <TextToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("text");
        setActiveTextRole("body");
        onCloseImages();
        setShowShapes(false);
        setShowClipart(false);
        setShowTemplates(false);
        setShowBackground(false);
      },
    },
    {
      id: "image" as const,
      label: t("images"),
      description: descriptions.image,
      icon: <ImageToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("image");
        setShowShapes(false);
        setShowClipart(false);
        setShowTemplates(false);
        setShowBackground(false);
        onOpenImages();
      },
    },
    {
      id: "clipart" as const,
      label: "Clipart",
      description: descriptions.clipart,
      icon: <ClipartToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("clipart");
        onCloseImages();
        setShowShapes(false);
        setShowTemplates(false);
        setShowBackground(false);
        setShowClipart(true);
      },
    },
    {
      id: "shapes" as const,
      label: t("shapes"),
      description: descriptions.shapes,
      icon: <ShapesToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("shapes");
        onCloseImages();
        setShowClipart(false);
        setShowTemplates(false);
        setShowBackground(false);
        setShowShapes(true);
      },
    },
    {
      id: "templates" as const,
      label: t("templates"),
      description: descriptions.templates,
      icon: <TemplatesToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("templates");
        onCloseImages();
        setShowShapes(false);
        setShowClipart(false);
        setShowBackground(false);
        setShowTemplates(true);
      },
    },
    {
      id: "background" as const,
      label: t("background"),
      description: descriptions.background,
      icon: <BackgroundToolIcon />,
      onClick: () => {
        if (readOnly) return;
        setActiveTool("background");
        onCloseImages();
        setShowShapes(false);
        setShowClipart(false);
        setShowTemplates(false);
        setShowBackground(true);
      },
    },
  ];

  return (
    <div className="relative">
      {!launcherOnly ? (
        <>
          {!toolsCollapsed ? (
            <h2 className="editor-heading mb-4 text-left">{locale === "pl" ? "Narzędzia" : "Tools"}</h2>
          ) : null}
          {readOnly ? (
            <div
              className={`mb-3 rounded-lg border border-white/10 bg-white/5 text-xs text-white/70 ${
                toolsCollapsed ? "p-2 text-center" : "p-3"
              }`}
              title={locale === "pl" ? "Tryb tylko do odczytu." : "Read-only mode."}
            >
              {toolsCollapsed ? "–" : locale === "pl" ? "Tryb tylko do odczytu." : "Read-only mode."}
            </div>
          ) : null}

          <div className={toolsCollapsed ? "space-y-2" : "space-y-3"}>
            {tools.map((tool) => (
              <div key={tool.id}>
                <button
                  type="button"
                  data-active={activeTool === tool.id}
                  data-onboarding-target={`tool-${tool.id}`}
                  className={`editor-tool-button ${
                    toolsCollapsed ? "!flex items-center justify-center !px-1 !py-2 text-center" : ""
                  }`}
                  onClick={() => {
                    tool.onClick();
                    if (!readOnly && tool.id !== "text") onCollapsedChange?.(true);
                  }}
                  disabled={readOnly}
                  title={toolsCollapsed ? tool.label : undefined}
                  aria-label={toolsCollapsed ? tool.label : undefined}
                >
                  <div className={`flex items-center ${toolsCollapsed ? "flex-col justify-center gap-2" : "gap-3"}`}>
                    <span className={`text-white ${toolsCollapsed ? "[&>svg]:h-8 [&>svg]:w-8" : ""}`}>
                      {tool.icon}
                    </span>
                    {toolsCollapsed ? (
                      <span className="max-w-full break-words text-[11px] font-semibold leading-tight text-white">
                        {tool.label}
                      </span>
                    ) : (
                      <div className="text-left">
                        <p className="text-[15px] font-semibold leading-none text-white">{tool.label}</p>
                        <p className="mt-1 text-xs text-white">{tool.description}</p>
                      </div>
                    )}
                  </div>
                </button>

                {tool.id === "text" && activeTool === "text" ? (
                  <div className={toolsCollapsed ? "mt-2 px-0" : "mt-2 px-6 px-1"}>
                    <button
                      type="button"
                      data-active={activeTextRole === "body"}
                      className="editor-subtool-button"
                      onClick={() => {
                        if (readOnly) return;
                        setActiveTextRole("body");
                        onAddTextRole("body");
                        onCollapsedChange?.(true);
                      }}
                      disabled={readOnly}
                    >
                      {descriptions.textBody}
                    </button>
                    <button
                      type="button"
                      data-active={activeTextRole === "heading"}
                      className="editor-subtool-button"
                      onClick={() => {
                        if (readOnly) return;
                        setActiveTextRole("heading");
                        onAddTextRole("heading");
                        onCollapsedChange?.(true);
                      }}
                      disabled={readOnly}
                    >
                      {descriptions.textHeading}
                    </button>
                    <button
                      type="button"
                      data-active={activeTextRole === "list"}
                      className="editor-subtool-button"
                      onClick={() => {
                        if (readOnly) return;
                        setActiveTextRole("list");
                        onAddTextRole("list");
                        onCollapsedChange?.(true);
                      }}
                      disabled={readOnly}
                    >
                      {descriptions.textList}
                    </button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </>
      ) : null}

      {showShapes ? (
        <div className={floatingOverlayClass()}>
          <div className="absolute inset-0" onMouseDown={() => closeToolPanel(() => setShowShapes(false))} />

          <div
            className={floatingPanelClass(shapesEntered)}
            style={floatingPanelStyle()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="editor-modal-header">
              <div className="text-sm font-semibold uppercase tracking-wide text-white">{t("shapes")}</div>
              <button
                type="button"
                className="editor-modal-close rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
                onClick={() => closeToolPanel(() => setShowShapes(false))}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-3 p-4">
              {SHAPE_MENU.map((shape) => (
                <button
                  key={shape.id}
                  type="button"
                  className="editor-icon-tile"
                  title={shape.label}
                  aria-label={shape.label}
                  onClick={() => {
                    if (readOnly) return;
                    setActiveTool("shapes");
                    onAddShape(shape.id);
                    setShowShapes(false);
                  }}
                  disabled={readOnly}
                >
                  <ShapeIcon id={shape.id} />
                </button>
              ))}
            </div>

            <div className="border-t border-white/10 px-4 pb-4">
              <button
                type="button"
                className="editor-outline-button mt-4 w-full"
                onClick={() => {
                  if (readOnly) return;
                  setActiveTool("shapes");
                  onAddRect();
                  setShowShapes(false);
                }}
                disabled={readOnly}
              >
                {t("decoration")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showImages ? (
        <div className={floatingOverlayClass()}>
          <div className="absolute inset-0" onMouseDown={() => closeToolPanel(onCloseImages)} />

          <div
            className={`${floatingPanelClass(imagesEntered)} ${imageAssetDragging ? "opacity-40" : ""}`}
            style={floatingPanelStyle()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="editor-modal-header">
              <div className="text-sm font-semibold uppercase tracking-wide text-white">{t("myImages")}</div>
              <button
                type="button"
                className="editor-modal-close rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
                onClick={() => closeToolPanel(onCloseImages)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>

            </div>

            <div className="max-h-[calc(100vh-160px)] space-y-4 overflow-auto p-4">
              <div className="grid grid-cols-2 gap-2">
                <label className="editor-outline-button cursor-pointer bg-white/6">
                  {uploading ? t("uploading") : t("upload")}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onUploadImage}
                    disabled={readOnly || uploading}
                  />
                </label>

                <label className="editor-outline-button cursor-pointer bg-white/6">
                  {t("bulk")}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onBulkUpload}
                    disabled={readOnly || uploading || bulkProgress?.running}
                  />
                </label>
              </div>

              {bulkProgress?.running ? (
                <div className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
                  <div className="mb-2 flex items-center justify-between text-xs font-semibold text-white">
                    <span>{t("uploadingProgress", { done: bulkProgress.done, total: bulkProgress.total })}</span>
                    <span>
                      {Math.round((Math.max(0, bulkProgress.done) / Math.max(1, bulkProgress.total)) * 100)}%
                    </span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-emerald-400 transition-all"
                      style={{
                        width: `${Math.min(100, Math.round((Math.max(0, bulkProgress.done) / Math.max(1, bulkProgress.total)) * 100))}%`
                      }}
                    />
                  </div>
                </div>
              ) : null}

              <ImageLibraryPanel
                onInsertAsset={onInsertLibraryAsset}
                refreshNonce={imageLibraryRefreshNonce}
                readOnly={readOnly}
                onAssetDragStart={() => setImageAssetDragging(true)}
                onAssetDragEnd={() => setImageAssetDragging(false)}
              />
            </div>
          </div>
        </div>
      ) : null}

      {showClipart ? (
        <div className={floatingOverlayClass()}>
          <div className="absolute inset-0" onMouseDown={() => closeToolPanel(() => setShowClipart(false))} />

          <div
            className={floatingPanelClass(clipartEntered)}
            style={floatingPanelStyle()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="editor-modal-header">
              <div className="text-sm font-semibold uppercase tracking-wide text-white">Clipart</div>
              <button
                type="button"
                className="editor-modal-close rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
                onClick={() => closeToolPanel(() => setShowClipart(false))}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(100vh-160px)] overflow-auto p-4">
              <ClipartLibraryPanel locale={locale} readOnly={readOnly} onInsertClipart={onInsertClipart} />
            </div>
          </div>
        </div>
      ) : null}

      {showBackground ? (
        <div className={floatingOverlayClass()}>
          <div className="absolute inset-0" onMouseDown={() => closeToolPanel(() => setShowBackground(false))} />

          <div
            className={floatingPanelClass(backgroundEntered)}
            style={floatingPanelStyle()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="editor-modal-header">
              <div className="text-sm font-semibold uppercase tracking-wide text-white">{t("background")}</div>
              <button
                type="button"
                className="editor-modal-close rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
                onClick={() => closeToolPanel(() => setShowBackground(false))}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="max-h-[calc(100vh-160px)] space-y-4 overflow-auto p-4">
              {backgroundPanelMode === "patterns" ? (
                <>
                  <button
                    type="button"
                    className={`editor-outline-button w-full bg-white/6 ${selectedPatternId ? "" : "ring-2 ring-white/20"}`}
                    onClick={() => {
                      if (readOnly) return;
                      setActiveTool("background");
                      setBackgroundPanelMode("solid");
                      onApplySolidBackgroundColor(activeBackgroundColor);
                    }}
                    disabled={readOnly}
                  >
                    {locale === "pl" ? "Jednolity kolor" : "Solid color"}
                  </button>

                  <div className="text-sm font-medium text-[#98a4bc]">
                    {locale === "pl" ? "Powtarzalne wzory SVG" : "Repeatable SVG patterns"}
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {BACKGROUND_PATTERNS.map((pattern) => (
                      <BackgroundPatternPreview
                        key={pattern.id}
                        title={pattern.title}
                        url={getBackgroundPatternUrl(pattern.fileName)}
                        tileW={pattern.tileW}
                        selected={selectedPatternId === pattern.id}
                        disabled={readOnly}
                        onClick={() => {
                          if (readOnly) return;
                          setActiveTool("background");
                          onApplyBackgroundPattern(pattern.id);
                        }}
                      />
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 rounded-xl border border-white/10 bg-white/5 p-1">
                    <button
                      type="button"
                      className="rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#272727]"
                      onClick={() => {
                        if (readOnly) return;
                        onApplySolidBackgroundColor(activeBackgroundColor);
                      }}
                      disabled={readOnly}
                    >
                      {locale === "pl" ? "Jednolity kolor" : "Solid color"}
                    </button>
                    <button
                      type="button"
                      className="rounded-lg px-3 py-2 text-sm font-semibold text-white/85 transition-colors hover:bg-white/10"
                      onClick={() => setBackgroundPanelMode("patterns")}
                    >
                      {locale === "pl" ? "Tło z wzorem" : "Pattern background"}
                    </button>
                  </div>

                  <div className="rounded-xl border border-white/10 bg-white/5 p-4">
                    <div className="mb-3 text-sm font-medium text-[#98a4bc]">
                      {locale === "pl" ? "Kolor tła" : "Background color"}
                    </div>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={activeBackgroundColor}
                        disabled={readOnly}
                        className="h-12 w-16 cursor-pointer rounded-lg border border-white/15 bg-transparent p-1 disabled:cursor-not-allowed disabled:opacity-50"
                        onChange={(event) => {
                          setActiveTool("background");
                          onApplySolidBackgroundColor(event.target.value);
                        }}
                        aria-label={locale === "pl" ? "Kolor tła" : "Background color"}
                      />
                      <input
                        type="text"
                        value={activeBackgroundColor}
                        readOnly
                        disabled={readOnly}
                        className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm font-semibold text-white outline-none focus:border-white/30 disabled:opacity-50"
                        aria-label={locale === "pl" ? "Kod koloru tła" : "Background color code"}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showTemplates ? (
        <div className={floatingOverlayClass()}>
          <div
            className="absolute inset-0"
            onMouseDown={() => closeToolPanel(() => setShowTemplates(false))}
          />

          <div
            className={floatingPanelClass(templatesEntered)}
            style={floatingPanelStyle()}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="editor-modal-header">
              <div className="text-sm font-semibold uppercase tracking-wide text-white">
                {t("templates")}
              </div>
              <button
                type="button"
                className="editor-modal-close rounded-md p-1 text-white/85 transition-colors hover:bg-white/5 hover:text-white"
                onClick={() => closeToolPanel(() => setShowTemplates(false))}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="m6 6 12 12M18 6 6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            {/* Tabs */}
            <div className="grid grid-cols-2 gap-2 border-b border-white/10 p-3">
              <button
                type="button"
                className={`editor-outline-button bg-white/6 ${templatesTab === "themes" ? "ring-2 ring-white/20" : ""}`}
                onClick={() => setTemplatesTab("themes")}
              >
                {t("themes")}
              </button>
              <button
                type="button"
                className={`editor-outline-button bg-white/6 ${templatesTab === "spreads" ? "ring-2 ring-white/20" : ""}`}
                onClick={() => setTemplatesTab("spreads")}
              >
                {t("spreads")}
              </button>
            </div>

            <div className="max-h-[calc(100vh-160px)] space-y-4 overflow-auto p-4">
              {templatesTab === "themes" ? (
                <>
                  <div className="text-sm font-medium text-[#98a4bc]">
                    {t("themesHint")}
                  </div>

                  {canCreateThemeTemplate ? (
                    <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/80">
                        {locale === "pl" ? "Utwórz motyw z projektu" : "Create theme from project"}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={themeTemplateName}
                          onChange={(event) => setThemeTemplateName(event.target.value)}
                          placeholder={locale === "pl" ? "Nazwa motywu" : "Theme name"}
                          className="min-w-0 flex-1 rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-white/30"
                          maxLength={80}
                        />
                        <button
                          type="button"
                          className="editor-outline-button shrink-0 bg-white/6 px-3"
                          onClick={handleCreateThemeTemplate}
                          disabled={!themeTemplateName.trim() || themeTemplateCreateBusy}
                        >
                          {themeTemplateCreateBusy
                            ? locale === "pl" ? "Zapis..." : "Saving..."
                            : locale === "pl" ? "Utwórz" : "Create"}
                        </button>
                      </div>
                      <textarea
                        value={themeTemplateTipText}
                        onChange={(event) => setThemeTemplateTipText(event.target.value)}
                        placeholder={
                          locale === "pl"
                            ? "Tipsy per strona, np.\n3: Tekst dla strony 3\n8: Tekst dla strony 8"
                            : "Page tips, e.g.\n3: Text for page 3\n8: Text for page 8"
                        }
                        className="mt-2 min-h-20 w-full resize-y rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white outline-none placeholder:text-white/35 focus:border-white/30"
                        maxLength={4000}
                      />
                      <div className="mt-2 rounded-md border border-white/10 bg-black/15 px-3 py-2 text-[11px] leading-5 text-[#cbd5e1]">
                        <div className="font-semibold text-white/80">
                          {locale === "pl" ? "Przyklad tipsow:" : "Tips example:"}
                        </div>
                        <div className="font-mono text-white/70">
                          {locale === "pl" ? "3: Dodaj zdjecie mamy" : "3: Add a photo of mom"}
                        </div>
                        <div className="font-mono text-white/70">
                          {locale === "pl" ? "8,16: Wpisz krotki opis" : "8,16: Add a short caption"}
                        </div>
                      </div>
                      <div className="mt-1 text-[11px] text-[#98a4bc]">
                        {locale === "pl"
                          ? "Format: numer strony: tekst. Możesz użyć 3,8: ten sam tekst. Tipsy nie trafią do PDF."
                          : "Format: page number: text. You can use 3,8: same text. Tips are excluded from PDF export."}
                      </div>
                    </div>
                  ) : null}

                  <div className="grid grid-cols-2 gap-3">
                    {themeTemplates.map((tpl) => {
                      const pagesLabel = t("themesPages", { pages: tpl.target_physical_pages });
                      const isGeneratedTheme = tpl.source === "generated";
                      const canDeleteTheme = Boolean(onDeleteThemeTemplate && isGeneratedTheme);
                      const deleteBusy = themeTemplateDeleteBusyId === tpl.id;
                      const copiedId = copiedThemeTemplateId === tpl.id;
                      return (
                        <div
                          key={tpl.id}
                          className="relative overflow-hidden rounded-xl border border-white/10 bg-white/5 text-white"
                        >
                          <button
                            type="button"
                            className="block w-full text-left transition-all hover:bg-white/10 disabled:opacity-50"
                            onClick={() => {
                              if (readOnly) return;
                              setActiveTool("templates");
                              onApplyTheme(tpl.id);
                              setShowTemplates(false);
                            }}
                            disabled={readOnly}
                            title={`${tpl.name} (${tpl.id})`}
                            aria-label={tpl.name}
                          >
                            <ThemeTemplateThumbnail theme={tpl} pagesLabel={pagesLabel} />
                            <div className="px-3 py-2">
                              <div className="truncate text-xs font-semibold text-white">
                                {tpl.name}
                              </div>
                              <div className="mt-1 truncate text-[11px] text-[#98a4bc]">
                                {pagesLabel}
                              </div>
                            </div>
                          </button>

                          {isGeneratedTheme ? (
                            <button
                              type="button"
                              className="absolute left-2 top-2 rounded-md border border-white/15 bg-black/55 px-2 py-1 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-white/20"
                              onClick={() => copyThemeTemplateId(tpl.id)}
                              title={
                                locale === "pl"
                                  ? `Kopiuj ID motywu: ${tpl.id}`
                                  : `Copy theme ID: ${tpl.id}`
                              }
                              aria-label={
                                locale === "pl"
                                  ? `Kopiuj ID motywu ${tpl.name}`
                                  : `Copy theme ID ${tpl.name}`
                              }
                            >
                              {copiedId ? "OK" : "ID"}
                            </button>
                          ) : null}

                          {canDeleteTheme ? (
                            <button
                              type="button"
                              className="absolute right-2 top-2 rounded-md border border-white/15 bg-black/55 px-2 py-1 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-red-600 disabled:opacity-50"
                              onClick={() => onDeleteThemeTemplate?.(tpl.id)}
                              disabled={deleteBusy}
                              title={locale === "pl" ? `Usuń motyw ${tpl.name}` : `Delete theme ${tpl.name}`}
                              aria-label={locale === "pl" ? `Usuń motyw ${tpl.name}` : `Delete theme ${tpl.name}`}
                            >
                              {deleteBusy ? (locale === "pl" ? "..." : "...") : locale === "pl" ? "Usuń" : "Delete"}
                            </button>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  {themeTemplates.length === 0 ? (
                    <div className="text-xs text-[#98a4bc]">
                      {locale === "pl" ? "Brak motywów dla tego formatu." : "No themes for this format."}
                    </div>
                  ) : null}

                  {/* English comment: One-level undo for the last applied theme. */}
                  {canUndoTheme ? (
                    <button
                      type="button"
                      className="editor-outline-button mt-3 w-full bg-white/6"
                      onClick={() => {
                        if (readOnly) return;
                        setActiveTool("templates");
                        onUndoTheme();
                      }}
                      disabled={readOnly}
                    >
                      {locale === "pl" ? "Cofnij ostatni motyw" : "Undo last theme"}
                    </button>
                  ) : null}

                  <div className="text-xs text-[#98a4bc]">
                    {t("themesNote")}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-medium text-[#98a4bc]">
                    {t("spreadsHint")}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {spreads.map((tpl) => (
                      <button
                        key={tpl.id}
                        type="button"
                        className="overflow-hidden rounded-xl border border-white/10 bg-white/5 text-left text-white transition-all hover:bg-white/10 disabled:opacity-50"
                        onClick={() => {
                          if (readOnly) return;
                          setActiveTool("templates");
                          onInsertSpreadTemplate(tpl.id);
                          setShowTemplates(false);
                        }}
                        disabled={readOnly}
                        title={tpl.name}
                        aria-label={tpl.name}
                      >
                        <div className="bg-white p-2">
                          <TemplateThumbnail
                            blocks={tpl.blocks}
                            formatGroup={activeFormatGroup}
                            className="h-20 w-full"
                          />
                        </div>
                        <div className="px-3 py-2">
                          <div className="truncate text-xs font-semibold text-white">{tpl.name}</div>
                          <div className="mt-1 truncate text-[11px] text-[#98a4bc]">{t("insertAtEnd")}</div>
                        </div>
                      </button>
                    ))}
                  </div>

                  {spreads.length === 0 ? (
                    <div className="text-sm text-[#98a4bc]">
                      {t("noSpreadsForFormat")}
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {!launcherOnly && propertiesPanel ? <div className="border-t border-white/10 pt-4">{propertiesPanel}</div> : null}

      {!launcherOnly && !toolsCollapsed && presenceLabel ? (
        <div className="mt-4 border-t border-white/10 pt-4">
          <div className="text-xs font-semibold uppercase tracking-wide text-white/45">
            {locale === "pl" ? "Aktywność" : "Activity"}
          </div>
          <div className="mt-2 rounded-lg border border-white/10 bg-white/5 p-3 text-xs leading-5 text-white/70">
            {presenceLabel}
          </div>
        </div>
      ) : null}
    </div>
  );
}
